import type { ReportFilterStateV1 } from "@meta-video/contracts";
import { TextInput, type TextInputProps } from "@mantine/core";
import { useDebouncedCallback } from "@mantine/hooks";
import {
  createContext,
  type ReactNode,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import {
  type AcknowledgedFilterChanges,
  useAcknowledgedFilterChanges,
} from "./useAcknowledgedFilterChanges.js";

const AD_NAME_SEARCH_DEBOUNCE_MS = 300;

export interface AdNameSearchInputProps {
  readonly className?: string;
  readonly filters: ReportFilterStateV1;
  readonly label?: string;
  readonly onChange: (filters: ReportFilterStateV1) => boolean | void;
  readonly placeholder?: string;
  readonly size?: TextInputProps["size"];
}

export interface AdNameSearchController {
  readonly flush: () => void;
  readonly submit: (isComposing: boolean) => void;
  readonly update: (value: string) => void;
  readonly value: string;
}

export interface AdNameSearchContextValue {
  readonly controller: AdNameSearchController;
  readonly filterChanges: AcknowledgedFilterChanges;
}

const AdNameSearchContext = createContext<AdNameSearchContextValue | null>(null);

interface AdNameSearchFieldProps extends Omit<AdNameSearchInputProps, "filters" | "onChange"> {
  readonly controller: AdNameSearchController;
}

export function useAdNameSearchController(
  {
    filterChanges,
    filters,
    flushOnUnmount = true,
  }: {
    readonly filterChanges: AcknowledgedFilterChanges;
    readonly filters: ReportFilterStateV1;
    readonly flushOnUnmount?: boolean;
  },
): AdNameSearchController {
  const [draft, setDraft] = useState(filters.adNameSearch ?? "");
  const latestFiltersRef = useRef(filters);
  const draftVersionRef = useRef(0);
  latestFiltersRef.current = filters;

  const commit = useDebouncedCallback((value: string, version: number) => {
    const normalizedValue = value.trim();
    const accepted = filterChanges.changeFilters({
      ...latestFiltersRef.current,
      adNameSearch: normalizedValue === "" ? null : normalizedValue,
    });
    if (!accepted) {
      draftVersionRef.current += 1;
      setDraft(latestFiltersRef.current.adNameSearch ?? "");
    } else if (draftVersionRef.current === version) {
      setDraft(normalizedValue);
    }
  }, {
    delay: AD_NAME_SEARCH_DEBOUNCE_MS,
    flushOnUnmount,
  });

  // Reconcile foreign snapshots before paint so an immediate unmount cannot flush
  // a draft into replacement filters. Self-authored snapshots keep the draft intact.
  useLayoutEffect(() => {
    if (filterChanges.consumeAcknowledgement()) return;
    draftVersionRef.current += 1;
    setDraft(latestFiltersRef.current.adNameSearch ?? "");
    commit.cancel();
  }, [commit, filterChanges.consumeAcknowledgement]);

  return {
    flush: commit.flush,
    submit: (isComposing) => {
      if (!isComposing) commit.flush();
    },
    update: (value) => {
      const version = draftVersionRef.current + 1;
      draftVersionRef.current = version;
      setDraft(value);
      commit(value, version);
    },
    value: draft,
  };
}

export function AdNameSearchProvider({
  children,
  filters,
  onChange,
}: {
  readonly children: ReactNode;
  readonly filters: ReportFilterStateV1;
  readonly onChange: (filters: ReportFilterStateV1) => boolean | void;
}) {
  const filterChanges = useAcknowledgedFilterChanges({ filters, onChange });
  const controller = useAdNameSearchController({
    filterChanges,
    filters,
    flushOnUnmount: false,
  });

  return (
    <AdNameSearchContext.Provider value={{ controller, filterChanges }}>
      {children}
    </AdNameSearchContext.Provider>
  );
}

export function useAdNameSearchContext(): AdNameSearchContextValue | null {
  return useContext(AdNameSearchContext);
}

export function AdNameSearchField({
  className,
  controller,
  label,
  placeholder,
  size,
}: AdNameSearchFieldProps) {
  return (
    <TextInput
      aria-label="Ad name contains"
      className={className}
      label={label}
      maxLength={256}
      onBlur={controller.flush}
      onChange={(event) => controller.update(event.currentTarget.value)}
      onKeyDown={(event) => {
        if (event.key === "Enter") controller.submit(event.nativeEvent.isComposing);
      }}
      placeholder={placeholder}
      size={size}
      value={controller.value}
    />
  );
}

function StandaloneAdNameSearchInput(props: AdNameSearchInputProps) {
  const filterChanges = useAcknowledgedFilterChanges(props);
  const controller = useAdNameSearchController({
    filterChanges,
    filters: props.filters,
  });
  return (
    <AdNameSearchField
      className={props.className}
      controller={controller}
      label={props.label}
      placeholder={props.placeholder}
      size={props.size}
    />
  );
}

export function AdNameSearchInput(props: AdNameSearchInputProps) {
  const shared = useAdNameSearchContext();
  if (shared === null) return <StandaloneAdNameSearchInput {...props} />;
  return (
    <AdNameSearchField
      className={props.className}
      controller={shared.controller}
      label={props.label}
      placeholder={props.placeholder}
      size={props.size}
    />
  );
}
